import { Transfer } from 'stores/domain/Transfer';
import BankStore from 'stores/trader/banks';
import DepositDialog from 'stores/ui/dialog/deposit';
import WithdrawalDialog from 'stores/ui/dialog/withdrawal';

interface PollTransferOptions {
    bankStore: BankStore;
    userId: string;
    amount?: number;
    transferId?: string;
    isDeposit: boolean;
    dialog: DepositDialog | WithdrawalDialog;
    transferInitiatedTime?: Date;
    onSuccess?: (transfer: Transfer) => void;
    onFailure?: (transfer: Transfer) => void;
    onTimeout?: () => void;
    pollingIntervalMs?: number;
    timeoutMs?: number;
}

export const pollTransferStatus = ({
    bankStore,
    userId,
    amount,
    transferId,
    isDeposit,
    dialog,
    transferInitiatedTime,
    onSuccess,
    onFailure,
    onTimeout,
    pollingIntervalMs = 2000,
    timeoutMs = 60000,
}: PollTransferOptions): { intervalId: NodeJS.Timeout; timeoutId: NodeJS.Timeout } => {
    const amountCents = amount ? amount * 100 : 0;

    const intervalId = setInterval(() => {
        bankStore.loadTransfers(userId, () => {
            const recentTransfer = transferId
                ? bankStore.transfers.find((transfer) => transfer.id === transferId)
                : bankStore.transfers.find((transfer) => {
                      // Check if this is the right type of transfer
                      if (isDeposit && !transfer.isDeposit) return false;
                      if (!isDeposit && transfer.isDeposit) return false;

                      // Check amount matches
                      if (transfer.amount !== amountCents) return false;

                      // Check if created after initiation
                      if (!transfer.createdTs) return false;
                      const transferCreatedTime = new Date(transfer.createdTs).getTime();
                      if (!transferInitiatedTime || transferCreatedTime < transferInitiatedTime.getTime()) return false;

                      // Transfer should be created within 2 minutes of initiation
                      const timeWindow = 120000;
                      if (transferCreatedTime - transferInitiatedTime.getTime() > timeWindow) return false;

                      return true;
                  });

            if (recentTransfer) {
                // Skip if still pending - keep polling
                if (recentTransfer.status === 'pending') {
                    return;
                }

                if (recentTransfer.status === 'failed') {
                    clearInterval(intervalId);
                    clearTimeout(timeoutId);
                    handleTransferFailure(recentTransfer, dialog);
                    onFailure?.(recentTransfer);
                } else if (recentTransfer.status === 'applied') {
                    clearInterval(intervalId);
                    clearTimeout(timeoutId);
                    onSuccess?.(recentTransfer);
                }
            }
        });
    }, pollingIntervalMs);

    const timeoutId = setTimeout(() => {
        clearInterval(intervalId);
        onTimeout?.();
    }, timeoutMs);

    return { intervalId, timeoutId };
};

const handleTransferFailure = (transfer: Transfer, dialog: DepositDialog | WithdrawalDialog) => {
    if (transfer.failureErrorMessage) {
        const { message, details } = transfer.failureErrorMessage;

        if (dialog instanceof WithdrawalDialog) {
            dialog.setFailed(message, details);
        } else if (dialog instanceof DepositDialog) {
            dialog.setStep('failed');
            dialog.setErrorTitle(message);
            dialog.setErrorMessage(details);
            dialog.setErrorCode(transfer.failureErrorMessage.code);
        }
    } else {
        // Fallback to legacy error handling
        const defaultTitle = dialog instanceof WithdrawalDialog ? 'Your withdrawal failed' : 'Deposit Failed';
        const defaultMessage = 'Review entered info and try again';

        if (dialog instanceof WithdrawalDialog) {
            dialog.setFailed(defaultTitle, defaultMessage);
        } else if (dialog instanceof DepositDialog) {
            dialog.setStep('failed');
            dialog.setErrorTitle(defaultTitle);
            dialog.setErrorMessage(defaultMessage);
        }
    }

    dialog.loading = false;
};
